import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, istToday } from "@/lib/dateFormat";
import { X } from "lucide-react";

const RAG = { Green: "bg-green-100 text-green-700", Amber: "bg-amber-100 text-amber-700", Red: "bg-red-100 text-red-700" };

export default function QcomSLAPerformancePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 8) + "01");
  const [dateTo, setDateTo] = useState(istToday());

  // Drill-down state
  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/qcom-sla-performance", { params: { date_from: dateFrom, date_to: dateTo } });
      setData(d);
    } catch (err) {
      if (err.response?.status === 403) toast.error(err.response.data?.detail || "Access denied");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const openCard = async (personName) => {
    setCardLoading(true);
    setCard(null);
    try {
      const { data: c } = await api.get("/reports/qcom-sla-performance/individual", { params: { target_name: personName, date_from: dateFrom, date_to: dateTo } });
      setCard(c);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report card");
    } finally { setCardLoading(false); }
  };

  const complianceBadge = (pct) => {
    if (pct >= 90) return "bg-green-100 text-green-700";
    if (pct >= 70) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };

  const fmtTs = (ts) => {
    if (!ts || ts.length < 16) return ts || "";
    const d = ts.slice(0, 10).split("-");
    return d.length === 3 ? `${d[2]}-${d[1]}-${d[0]} ${ts.slice(11, 16)}` : ts;
  };

  return (
    <div data-testid="sla-performance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Qcom SLA Performance</h1>
          <p className="text-sm text-zinc-500 mt-1">Team and individual SLA compliance for Quick Commerce orders</p>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs rounded-sm w-36" data-testid="sla-date-from" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs rounded-sm w-36" data-testid="sla-date-to" />
        <Button onClick={load} variant="outline" className="rounded-sm h-8 text-xs">Refresh</Button>
      </div>

      {data && <p className="text-xs text-zinc-500 mb-4">{data.total_orders} Qcom orders in period {fmtDate(data.period?.from)} to {fmtDate(data.period?.to)}</p>}

      {/* Leaderboard Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="sla-leaderboard">
        <table className="data-table w-full">
          <thead><tr><th>Rank</th><th>Name</th><th className="num">Points</th><th className="num">Total Stages</th><th className="num">On Time</th><th className="num">Amber</th><th className="num">Red Breach</th><th className="num">Compliance %</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            !data || (data.by_individual || []).length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">No data for this period</td></tr> :
            (data.by_individual || []).map((p, i) => (
              <tr key={i} data-testid={`perf-row-${i}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => openCard(p.name)}>
                <td className="font-mono text-xs font-bold text-zinc-400">{i + 1}</td>
                <td className="text-xs font-medium text-blue-700 hover:underline">{p.name}</td>
                <td className="num font-mono text-xs font-bold">{p.points || 0}</td>
                <td className="num font-mono text-xs">{p.total}</td>
                <td className="num font-mono text-xs text-green-600">{p.on_time}</td>
                <td className="num font-mono text-xs text-amber-600">{p.amber}</td>
                <td className="num font-mono text-xs text-red-600">{p.red}</td>
                <td className="num"><span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${complianceBadge(p.compliance)}`}>{p.compliance}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ INDIVIDUAL REPORT CARD MODAL ═══ */}
      <Dialog open={!!card || cardLoading} onOpenChange={() => { setCard(null); setCardLoading(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sla-report-card-modal">
          {cardLoading && !card && <p className="text-xs text-zinc-400 text-center py-12">Loading report card...</p>}
          {card && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">{card.name} — SLA Report Card</DialogTitle>
              </DialogHeader>

              {/* SECTION 1: Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="sla-card-summary">
                <div className="border border-zinc-200 p-3 rounded-sm">
                  <p className="text-[10px] text-zinc-400 uppercase">Name & Role</p>
                  <p className="text-sm font-semibold">{card.name}</p>
                  <p className="text-[10px] text-zinc-500">{card.role}</p>
                </div>
                <div className="border border-zinc-200 p-3 rounded-sm">
                  <p className="text-[10px] text-zinc-400 uppercase">Total Points</p>
                  <p className={`text-xl font-mono font-bold ${card.total_points >= 0 ? 'text-green-700' : 'text-red-600'}`}>{card.total_points}</p>
                </div>
                <div className="border border-zinc-200 p-3 rounded-sm">
                  <p className="text-[10px] text-zinc-400 uppercase">Total Stages</p>
                  <p className="text-xl font-mono font-bold">{card.total_stages}</p>
                </div>
                <div className="border border-zinc-200 p-3 rounded-sm">
                  <p className="text-[10px] text-zinc-400 uppercase">Compliance</p>
                  <p className="text-xl font-mono font-bold">{card.compliance}%</p>
                </div>
                <div className="border border-zinc-200 p-3 rounded-sm">
                  <p className="text-[10px] text-zinc-400 uppercase">Period</p>
                  <p className="text-xs font-mono">{fmtDate(card.period?.from)}</p>
                  <p className="text-xs font-mono">to {fmtDate(card.period?.to)}</p>
                </div>
              </div>

              {/* SECTION 2: Points Earned */}
              <div className="mb-4" data-testid="sla-card-earned">
                <h3 className="text-xs font-semibold text-green-800 mb-2 uppercase">Points Earned ({(card.points_earned || []).length} entries)</h3>
                {(card.points_earned || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 py-3 text-center border border-dashed border-zinc-200 rounded-sm">No points earned in this period</p>
                ) : (
                  <div className="border border-zinc-200 overflow-x-auto rounded-sm">
                    <table className="data-table w-full">
                      <thead><tr><th>Date & Time</th><th>Order</th><th>Stage</th><th className="num">Time Taken</th><th className="num">SLA Threshold</th><th className="num">Points</th><th>Result</th></tr></thead>
                      <tbody>
                        {card.points_earned.map((e, i) => (
                          <tr key={i}>
                            <td className="text-[10px] font-mono text-zinc-600">{fmtTs(e.timestamp)}</td>
                            <td className="font-mono text-xs font-semibold">{e.order_number}</td>
                            <td className="text-xs">{e.stage}</td>
                            <td className="num font-mono text-xs">{e.time_taken}</td>
                            <td className="num font-mono text-xs text-zinc-500">{e.sla_threshold}</td>
                            <td className="num font-mono text-xs font-bold text-green-700">+{e.points}</td>
                            <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${RAG[e.result] || ''}`}>{e.result}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* SECTION 3: Points Lost */}
              <div className="mb-4" data-testid="sla-card-lost">
                <h3 className="text-xs font-semibold text-red-800 mb-2 uppercase">Points Lost ({(card.points_lost || []).length} entries)</h3>
                {(card.points_lost || []).length === 0 ? (
                  <p className="text-xs text-zinc-400 py-3 text-center border border-dashed border-zinc-200 rounded-sm">No SLA breaches in this period</p>
                ) : (
                  <div className="border border-zinc-200 overflow-x-auto rounded-sm">
                    <table className="data-table w-full">
                      <thead><tr><th>Date & Time</th><th>Order</th><th>Stage Breached</th><th className="num">Time Taken</th><th className="num">SLA Threshold</th><th className="num">How Late</th><th className="num">Points</th></tr></thead>
                      <tbody>
                        {card.points_lost.map((e, i) => (
                          <tr key={i}>
                            <td className="text-[10px] font-mono text-zinc-600">{fmtTs(e.timestamp)}</td>
                            <td className="font-mono text-xs font-semibold">{e.order_number}</td>
                            <td className="text-xs">{e.stage}</td>
                            <td className="num font-mono text-xs">{e.time_taken}</td>
                            <td className="num font-mono text-xs text-zinc-500">{e.sla_threshold}</td>
                            <td className="num font-mono text-xs text-red-600">{e.how_late}</td>
                            <td className="num font-mono text-xs font-bold text-red-600">{e.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* SECTION 4: Scorecard Logic (pinned reference) */}
              <div data-testid="sla-card-logic">
                <h3 className="text-xs font-semibold text-zinc-600 mb-2 uppercase border-t border-zinc-200 pt-3">Scorecard Logic — SLA Rules Reference</h3>
                <div className="border border-zinc-200 overflow-x-auto rounded-sm">
                  <table className="data-table w-full">
                    <thead><tr><th>Stage</th><th className="num">SLA Threshold</th><th className="num">On Time (Green)</th><th className="num">At Risk (Amber)</th><th className="num">Breached (Red)</th></tr></thead>
                    <tbody>
                      {(card.scorecard_logic || []).map((s, i) => (
                        <tr key={i}>
                          <td className="text-xs">{s.stage}</td>
                          <td className="num font-mono text-xs">{s.threshold}</td>
                          <td className="num font-mono text-xs text-green-700 font-semibold">{s.green_points}</td>
                          <td className="num font-mono text-xs text-amber-600 font-semibold">{s.amber_points}</td>
                          <td className="num font-mono text-xs text-red-600 font-semibold">{s.red_points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
